import React, { useEffect, useMemo, useState } from 'react'
import './CfoSite.css'
import { ServerApiClient, type AccountingProviderDescriptor } from '../../data/ServerApiClient'
import { CfoBrandMark, navigateTo } from './CfoSiteShared'
import { readSelectedSignupPlan, type SignupPlan } from './cfoSignupPlan'

type Phase = 'form' | 'submitting' | 'connecting' | 'check_email'

const PLAN_LABELS: Record<string, string> = { essentials: 'Essentials', pro: 'Pro', enterprise: 'Enterprise', partner: 'Partner' }

function QuickBooksMark() {
  return (
    <span className="cfo-provider-mark cfo-provider-mark-qb" aria-hidden="true">
      <svg viewBox="0 0 28 28" role="img">
        <circle cx="14" cy="14" r="14" fill="#2CA01C" />
        <path d="M7.2 14a5.1 5.1 0 0 1 5.1-5.1h1.5v2.8h-1.5a2.3 2.3 0 1 0 0 4.6h1.5v2.8h-1.5A5.1 5.1 0 0 1 7.2 14Zm7-5.1h1.5a5.1 5.1 0 1 1 0 10.2h-1.5v-2.8h1.5a2.3 2.3 0 1 0 0-4.6h-1.5V8.9Z" fill="#fff" />
      </svg>
    </span>
  )
}

function XeroMark() {
  return (
    <span className="cfo-provider-mark cfo-provider-mark-xero" aria-hidden="true">
      <svg viewBox="0 0 28 28" role="img">
        <circle cx="14" cy="14" r="14" fill="#13B5EA" />
        <text x="14" y="17.1" textAnchor="middle" fill="#fff" fontFamily="Arial, sans-serif" fontSize="7.2" fontWeight="700">xero</text>
      </svg>
    </span>
  )
}

function SageIntacctMark() {
  return (
    <span className="cfo-provider-mark cfo-provider-mark-sage-intacct" aria-hidden="true">
      <svg viewBox="0 0 28 28" role="img">
        <circle cx="14" cy="14" r="14" fill="#00A376" />
        <path d="M7 18.5V9.4h8.1c3.3 0 5.3 1.6 5.3 4.5 0 3-2 4.6-5.3 4.6H7Zm4-3h3.7c1.2 0 1.8-.5 1.8-1.6s-.6-1.5-1.8-1.5H11v3.1Z" fill="#fff" />
      </svg>
    </span>
  )
}

function SageAccountingMark() {
  return (
    <span className="cfo-provider-mark cfo-provider-mark-sage" aria-hidden="true">
      <svg viewBox="0 0 28 28" role="img">
        <circle cx="14" cy="14" r="14" fill="#00D639" />
        <text x="14" y="17" textAnchor="middle" fill="#111" fontFamily="Arial, sans-serif" fontSize="7" fontWeight="800">SAGE</text>
      </svg>
    </span>
  )
}

function NetSuiteMark() {
  return (
    <span className="cfo-provider-mark cfo-provider-mark-netsuite" aria-hidden="true">
      <svg viewBox="0 0 28 28" role="img">
        <circle cx="14" cy="14" r="14" fill="#1B5A7A" />
        <path d="M7.2 19V9h3.4l4.2 5.7V9h3.7v10h-3.3L11 13.3V19H7.2Z" fill="#fff" />
      </svg>
    </span>
  )
}

const FALLBACK_PROVIDERS: AccountingProviderDescriptor[] = [
  { provider: 'quickbooks', display_name: 'QuickBooks', product_family: 'intuit_quickbooks_online', logo_key: 'quickbooks', status: 'active', connector_enabled: true, self_serve_enabled: true, selection_kind: 'realm', auth_schemes: ['oauth2_authorization_code'], environments: ['production'], supported_datasets: [], webhook_supported: false, data_use_policy: 'customer_no_training' },
  { provider: 'xero', display_name: 'Xero', product_family: 'xero_accounting', logo_key: 'xero', status: 'private_beta', connector_enabled: false, self_serve_enabled: false, selection_kind: 'organization', auth_schemes: ['oauth2_authorization_code'], environments: ['production'], supported_datasets: [], webhook_supported: true, data_use_policy: 'xero_no_training' },
  { provider: 'sage_intacct', display_name: 'Sage Intacct', product_family: 'sage_intacct', logo_key: 'sage_intacct', status: 'planned', connector_enabled: false, self_serve_enabled: false, selection_kind: 'entity_and_book', auth_schemes: [], environments: ['production'], supported_datasets: [], webhook_supported: false, data_use_policy: 'customer_no_training' },
  { provider: 'sage_accounting', display_name: 'Sage Accounting', product_family: 'sage_business_cloud_accounting', logo_key: 'sage_accounting', status: 'planned', connector_enabled: false, self_serve_enabled: false, selection_kind: 'business', auth_schemes: [], environments: ['production'], supported_datasets: [], webhook_supported: false, data_use_policy: 'customer_no_training' },
  { provider: 'netsuite', display_name: 'NetSuite', product_family: 'oracle_netsuite', logo_key: 'netsuite', status: 'planned', connector_enabled: false, self_serve_enabled: false, selection_kind: 'subsidiary_and_book', auth_schemes: [], environments: ['production'], supported_datasets: [], webhook_supported: false, data_use_policy: 'customer_no_training' },
]

export function AccountingProviderMark({ provider }: { provider: string }) {
  if (provider === 'quickbooks') return <QuickBooksMark />
  if (provider === 'xero') return <XeroMark />
  if (provider === 'sage_intacct') return <SageIntacctMark />
  if (provider === 'sage_accounting') return <SageAccountingMark />
  return <NetSuiteMark />
}

export function CfoSignupPage() {
  const api = useMemo(() => new ServerApiClient(), [])
  const [fullName, setFullName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>('form')
  const [xeroSelfServeEnabled, setXeroSelfServeEnabled] = useState(false)
  const [providers, setProviders] = useState<AccountingProviderDescriptor[]>(FALLBACK_PROVIDERS)
  // The marketing site (fusegrid.io) hands off the chosen plan via ?plan=…, so
  // the visitor lands on the package they picked. Sent with the signup so the
  // server records it on the tenant and starts the 14-day full-Pro trial;
  // localStorage covers a reload that drops the query string.
  const selectedPlan = useMemo<SignupPlan | null>(() => {
    return readSelectedSignupPlan()
  }, [])
  useEffect(() => {
    if (selectedPlan) {
      try { window.localStorage.setItem('fg_selected_plan', selectedPlan) } catch { /* ignore */ }
    }
  }, [selectedPlan])
  useEffect(() => {
    void Promise.allSettled([api.getAccountingProviders(), api.getXeroAvailability()]).then(([catalogResult, xeroResult]) => {
      const catalog = catalogResult.status === 'fulfilled'
        && Array.isArray(catalogResult.value?.providers)
        && catalogResult.value.providers.length > 0
        ? catalogResult.value.providers
        : FALLBACK_PROVIDERS
      const xeroAvailability = xeroResult.status === 'fulfilled' ? xeroResult.value : null
      setProviders(catalog.map((provider) => provider.provider === 'xero' && xeroAvailability
        ? { ...provider, connector_enabled: xeroAvailability.connector_enabled, self_serve_enabled: xeroAvailability.self_serve_enabled }
        : provider))
      setXeroSelfServeEnabled(Boolean(xeroAvailability?.connector_enabled && xeroAvailability?.self_serve_enabled))
    })
  }, [api])
  // "Sign up with Xero" without a Xero-verified email: the server emailed a confirmation link and
  // creates the account only when it is completed. The link lands here with the token in the hash.
  useEffect(() => {
    const xeroIdentity = new URLSearchParams(window.location.search).get('xero_identity')
    const verifyToken = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('xero_verify')
    if (xeroIdentity === 'verify_email') {
      window.history.replaceState({}, '', window.location.pathname)
      setPhase('check_email')
      return
    }
    if (!verifyToken) return
    window.history.replaceState({}, '', window.location.pathname)
    setPhase('submitting')
    void api.verifyXeroSignupEmail(verifyToken)
      .then(() => api.createXeroIdentityAuthorizeUrl(`${window.location.origin}${window.location.pathname}`))
      .then((out) => {
        if (!out.authorize_url) throw new Error('Could not continue Xero sign up.')
        window.location.href = out.authorize_url
      })
      .catch((err: any) => {
        setPhase('form')
        setError(String(err?.message || err || 'Could not confirm your email.'))
      })
  }, [api])
  useEffect(() => {
    const ready = new URLSearchParams(window.location.search).get('xero_identity') === 'ready'
    if (!ready) return
    setPhase('submitting')
    void api.exchangeXeroIdentitySession().then(() => {
      window.history.replaceState({}, '', window.location.pathname)
      navigateTo('/cfo/experiences')
    }).catch((err: any) => {
      setPhase('form')
      setError(String(err?.message || err || 'Could not complete Xero sign in.'))
    })
  }, [api])
  // Attribution handed through from marketing surfaces (?utm_source=live_demo
  // from the in-demo banner). Persisted like the plan so a reload that drops
  // the query string keeps the source; sent with signup for funnel reporting.
  const utmSource = useMemo<string | null>(() => {
    try {
      const fromQuery = new URLSearchParams(window.location.search).get('utm_source')?.trim().toLowerCase()
      if (fromQuery) {
        window.localStorage.setItem('fg_signup_utm_source', fromQuery)
        return fromQuery
      }
      return window.localStorage.getItem('fg_signup_utm_source')?.trim().toLowerCase() || null
    } catch { /* ignore */ }
    return null
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!fullName.trim()) return setError('Please enter your name.')
    if (!companyName.trim()) return setError('Please enter your company name.')
    if (!email.trim()) return setError('Please enter your work email.')
    if (password.trim().length < 8) return setError('Password must be at least 8 characters.')
    setPhase('submitting')
    try {
      const res = await api.signup({
        email: email.trim(),
        password: password.trim(),
        full_name: fullName.trim(),
        company_name: companyName.trim(),
        phone: phone.trim() || null,
        plan: selectedPlan,
        utm_source: utmSource,
      })
      // Hand off to QuickBooks. After Intuit consent the server callback binds
      // the connection to this workbook, starts the first sync, and redirects
      // back to the branded "sync started" page.
      const redirect = `${window.location.origin}/onboarding/sync-started?provider=quickbooks`
      const out = await api.createQuickBooksAuthorizeUrl({
        intent: 'self_signup',
        workbook_id: res.workbook_id,
        redirect,
      })
      if (!out?.authorize_url) throw new Error('Could not start the QuickBooks connection.')
      setPhase('connecting')
      window.location.href = out.authorize_url
    } catch (err: any) {
      setPhase('form')
      setError(String(err?.message || err || 'Something went wrong. Please try again.'))
    }
  }

  const startXeroIdentity = () => {
    if (!xeroSelfServeEnabled) return
    setError(null)
    setPhase('submitting')
    void api.createXeroIdentityAuthorizeUrl(`${window.location.origin}${window.location.pathname}`).then((out) => {
      if (!out.authorize_url) throw new Error('Could not start Xero sign up.')
      window.location.href = out.authorize_url
    }).catch((err: any) => {
      setPhase('form')
      setError(String(err?.message || err || 'Could not start Xero sign up.'))
    })
  }

  if (phase === 'connecting') {
    return (
      <div className="cfo-site">
        <div className="cfo-center">
          <div className="cfo-panel cfo-center-text">
            <div className="cfo-orb" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
                <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"
                  style={{ stroke: '#fff', strokeWidth: 2, strokeLinecap: 'round' }} />
              </svg>
            </div>
            <h1>Connecting to QuickBooks…</h1>
            <p className="sub">Taking you to Intuit to authorize CFO Core. Hang tight.</p>
          </div>
        </div>
      </div>
    )
  }

  if (phase === 'check_email') {
    return (
      <div className="cfo-site">
        <div className="cfo-center">
          <div className="cfo-panel cfo-center-text">
            <CfoBrandMark sub="by FuseGrid" />
            <h1>Check your email</h1>
            <p className="sub">We sent a link to the email address on your Xero login. Open it to confirm the address and finish signing up with Xero. No FuseGrid account is created until you do; the link expires in 24 hours.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="cfo-site">
      <div className="cfo-center">
        <form className="cfo-panel" onSubmit={submit}>
          <CfoBrandMark sub="by FuseGrid" />
          {selectedPlan && (
            <div className="cfo-plan-pill">Starting your {PLAN_LABELS[selectedPlan]} plan trial · free, no card</div>
          )}
          <h1>Create your CFO Core</h1>
          <p className="sub">A minute of basics, then connect your accounting platform. We’ll build your CFO Core and email you when it’s ready.</p>
          {error && <div className="cfo-error">{error}</div>}
          <div className="cfo-field">
            <label htmlFor="su-name">Full name</label>
            <input id="su-name" value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" placeholder="Jane Doe" />
          </div>
          <div className="cfo-field">
            <label htmlFor="su-company">Company name</label>
            <input id="su-company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} autoComplete="organization" placeholder="Acme, Inc." />
          </div>
          <div className="cfo-field">
            <label htmlFor="su-email">Work email</label>
            <input id="su-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="jane@acme.com" />
          </div>
          <div className="cfo-field">
            <label htmlFor="su-password">Password</label>
            <input id="su-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" placeholder="At least 8 characters" />
          </div>
          <div className="cfo-field">
            <label htmlFor="su-phone">Phone <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</span></label>
            <input id="su-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" placeholder="(555) 123-4567" />
          </div>
          <div className="cfo-provider-actions" aria-label="Choose your accounting provider">
            {providers.map((provider) => {
              const isQuickBooks = provider.provider === 'quickbooks'
              const isXero = provider.provider === 'xero'
              const enabled = isQuickBooks || (isXero && xeroSelfServeEnabled)
              const statusLabel = provider.status === 'private_beta' ? ' · private beta' : provider.status === 'planned' ? ' · planned' : ''
              return (
                <button
                  key={provider.provider}
                  type={isQuickBooks ? 'submit' : 'button'}
                  className={`cfo-btn cfo-provider-btn cfo-provider-btn-${provider.logo_key}`}
                  disabled={phase !== 'form' || !enabled}
                  onClick={isXero ? startXeroIdentity : undefined}
                  title={enabled ? `Connect ${provider.display_name}` : `${provider.display_name} is ${provider.status.replace('_', ' ')}`}
                >
                  <AccountingProviderMark provider={provider.provider} />
                  <span>{isQuickBooks && phase === 'submitting' ? 'Creating…' : `${provider.display_name}${statusLabel}`}</span>
                </button>
              )
            })}
          </div>
          <p className="cfo-provider-note">
            {xeroSelfServeEnabled
              ? 'Xero runs the complete CFO suite — statements, ratios, A/R, 13-week cash, forecasts, close, and reporting, plus transaction-level ledger detail rebuilt from your invoices, bills, payments, and bank transactions — cross-checked to the penny against Xero’s own reports every month.'
              : 'Xero access is currently by invitation while Xero certification completes. Every invited firm gets the complete CFO suite — statements through forecasts to transaction-level ledger detail — reconciled to the penny against Xero’s own reports.'}
          </p>
          <p className="cfo-fineprint">
            By continuing you agree to our <a onClick={() => navigateTo('/terms')} style={{ cursor: 'pointer' }}>Terms</a> and{' '}
            <a onClick={() => navigateTo('/privacy')} style={{ cursor: 'pointer' }}>Privacy Policy</a>.
          </p>
          <div className="cfo-altline">
            Already have an account? <a onClick={() => navigateTo('/login')}>Sign in</a>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CfoSignupPage
